import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import PageHeader from "@/components/PageHeader";
import { Package, Truck, Clock, CheckCircle2, ExternalLink, Search, ArrowUpDown, ChevronRight } from "lucide-react";

// Map raw shipment statuses to the 3 user-facing buckets.
const bucketOf = (status) => {
  if (status === "delivered") return "delivered";
  if (status === "shipped") return "transit";
  return "pending"; // requested, paid, shipping_requested, shipping_paid, pending…
};

const BUCKET_META = {
  pending: { label: "hc.pending", cls: "bg-amber-100 text-amber-700", Icon: Clock, rank: 0 },
  transit: { label: "hc.inTransit", cls: "bg-blue-100 text-blue-700", Icon: Truck, rank: 1 },
  delivered: { label: "hc.delivered", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, rank: 2 },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(document.documentElement.lang || "fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const ts = (iso) => { try { return new Date(iso).getTime() || 0; } catch { return 0; } };

export default function Shipments() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { scope, selectedId } = useEstablishment();
  const [shipments, setShipments] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("date_desc");

  async function load() {
    try { setShipments(await api(scope("/shipments/overview"))); }
    catch (e) { toast.error(e.message); setShipments([]); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedId]);

  const all = shipments || [];
  const counts = useMemo(() => {
    const c = { all: all.length, pending: 0, transit: 0, delivered: 0 };
    all.forEach((s) => { c[bucketOf(s.status)] += 1; });
    return c;
  }, [all]);

  const visible = useMemo(() => {
    let rows = filter === "all" ? all : all.filter((s) => bucketOf(s.status) === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((s) =>
        (s.customer_name || "").toLowerCase().includes(q) ||
        (s.item_description || "").toLowerCase().includes(q) ||
        (s.tracking_number || "").toLowerCase().includes(q)
      );
    }
    const sorted = [...rows];
    if (sort === "date_desc") sorted.sort((a, b) => ts(b.shipped_at || b.created_at) - ts(a.shipped_at || a.created_at));
    else if (sort === "date_asc") sorted.sort((a, b) => ts(a.shipped_at || a.created_at) - ts(b.shipped_at || b.created_at));
    else if (sort === "status") sorted.sort((a, b) => BUCKET_META[bucketOf(a.status)].rank - BUCKET_META[bucketOf(b.status)].rank);
    return sorted;
  }, [all, filter, query, sort]);

  const FILTERS = [
    { key: "all", label: t("hc.all") },
    { key: "pending", label: t(BUCKET_META.pending.label) },
    { key: "transit", label: t(BUCKET_META.transit.label) },
    { key: "delivered", label: t(BUCKET_META.delivered.label) },
  ];

  const openDeclaration = (s) => {
    if (s.declaration_id) navigate(`/b2b/declarations?shipment=${s.declaration_id}`);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        title={t("hc.shipments")}
        subtitle={t("hc.shipmentsSubtitle")}
        testid="shipments-header"
      />

      {shipments !== null && all.length > 0 && (
        <>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="shipments-status-filter">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`shipments-filter-${f.key}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  filter === f.key ? "bg-findo-navy text-white" : "bg-white border border-border text-slate-600 hover:bg-slate-50"
                }`}
              >
                {f.label}
                <span className={`ml-2 text-xs ${filter === f.key ? "text-white/80" : "text-slate-400"}`}>{counts[f.key] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("hc.searchByCustomerItemOr")}
                data-testid="shipments-search"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-findo-navy/30"
              />
            </div>
            <div className="relative">
              <ArrowUpDown size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="shipments-sort"
                className="h-10 pl-9 pr-8 rounded-xl border border-border bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-findo-navy/30 appearance-none cursor-pointer"
              >
                <option value="date_desc">{t("hc.dateNewest")}</option>
                <option value="date_asc">{t("hc.dateOldest")}</option>
                <option value="status">{t("hc.status")}</option>
              </select>
            </div>
          </div>
        </>
      )}

      {shipments === null ? (
        <div className="space-y-3" data-testid="shipments-skeleton">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : all.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="shipments-empty">
          <Package size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{t("hc.noShipmentsYet")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="shipments-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 font-semibold">{t("hc.customer")}</th>
                  <th className="px-4 py-3 font-semibold">{t("hc.item")}</th>
                  <th className="px-4 py-3 font-semibold">{t("hc.carrier")}</th>
                  <th className="px-4 py-3 font-semibold">{t("hc.tracking")}</th>
                  <th className="px-4 py-3 font-semibold">{t("hc.status")}</th>
                  <th className="px-4 py-3 font-semibold">{t("hc.date")}</th>
                  <th className="px-4 py-3 font-semibold w-8" />
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr data-testid="shipments-filter-empty">
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-400">{t("hc.noShipmentMatchesYourSearch")}</td>
                  </tr>
                ) : (
                  visible.map((s) => {
                    const b = BUCKET_META[bucketOf(s.status)];
                    const BIcon = b.Icon;
                    const clickable = !!s.declaration_id;
                    return (
                      <tr
                        key={s.id}
                        onClick={() => openDeclaration(s)}
                        className={`border-t border-border transition-colors ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}
                        data-testid={`shipment-row-${s.id}`}
                        title={clickable ? t("hc.openTheDeclarationToManage") : ""}
                      >
                        <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap" data-testid={`shipment-client-${s.id}`}>
                          {s.customer_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={s.item_description} data-testid={`shipment-item-${s.id}`}>
                          {s.item_description || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`shipment-carrier-${s.id}`}>
                          {s.carrier || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`shipment-tracking-${s.id}`}>
                          {s.tracking_number ? (
                            s.tracking_url ? (
                              <a href={s.tracking_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-findo-navy font-semibold underline">
                                {s.tracking_number} <ExternalLink size={12} />
                              </a>
                            ) : s.tracking_number
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-testid={`shipment-status-${s.id}`}>
                          <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${b.cls}`}>
                            <BIcon size={12} /> {t(b.label)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`shipment-date-${s.id}`}>
                          {fmtDate(s.shipped_at || s.created_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {clickable && <ChevronRight size={16} />}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
